<script lang="ts">
import { defineComponent } from 'vue'
import NrIconButton from '@nextrequestco/components/src/components/NrIconButton'
import NrModal from '@nextrequestco/components/src/components/NrModal'
import NrTooltip from '@nextrequestco/components/src/components/NrTooltip'
import NrFormInput from '@nextrequestco/components/src/components/NrFormInput'
import helpers from '../../../utils/helpers'
import consumer from 'src/utils/action_cable_consumer'

import { mapActions } from 'vuex'

export default defineComponent({
  name: 'request-expiration-date',
  components: { NrIconButton, NrTooltip, NrModal, NrFormInput },
  props: {
    prettyId: { type: String },
    exempt: { type: Boolean },
    expiration: { type: String }
  },
  data() {
    return {
      showModal: false,
      updating: false,
      newExempt: this.exempt,
      exemptedDocuments: false,
      subscription: null,
      currentJobId: null
    }
  },

  mounted() {
    this.subscribeToUserJobChannel()
  },

  unmounted() {
    this.unsubscribeToUserJobChannel()
  },

  methods: {
    ...mapActions('request_show', ['updateRequest', 'fetchTimeline']),
    ...mapActions('documents', ['bulkDocumentActions']),

    subscribeToUserJobChannel() {
      const self = this
      this.subscription = consumer.subscriptions.create(
        // Subscribe to channel
        {
          channel: 'UserJobChannel',
          user_id: this.$store.state.currentUser.id
        },
        {
          received(data) {
            if (data.job_type !== 'exempt_from_retention') return
            if (data.job_id !== self.currentJobId) return

            self.setupToastMessage(data)
          }
        }
      )
    },

    unsubscribeToUserJobChannel() {
      if (consumer.connection.isOpen()) {
        consumer.subscriptions.remove(this.subscription)
      }
    },

    setupToastMessage(data) {
      if (helpers.backgroundJobComplete(data.status)) {
        this.setSuccessToastMessage()
        this.fetchTimeline({ pretty_id: this.prettyId })
      } else if (helpers.backgroundJobFailed(data.status)) {
        this.setFailedToastMessage()
      }
    },

    setSuccessToastMessage() {
      this.$toasted.success('Successfully updated document(s) retention status')
    },

    setFailedToastMessage() {
      this.$toasted.error('Failed to update document(s) retention status')
    },

    async handleSaveExempt() {
      this.updating = true
      try {
        await this.updateRequest({
          request_id: this.prettyId,
          exempt_from_retention: this.newExempt
        })
        this.$toasted.success('Successfully updated request retention status')
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error) {
        this.$toasted.error('Failed to update request retention status')
        this.updating = false
      }

      if (this.exemptedDocuments) {
        try {
          await this.bulkDocumentActions({
            bulk_action: 'update_exempt_from_retention',
            all_selected: true,
            request_id: this.prettyId,
            exempt_from_retention: true
          }).then((xhr) => {
            if (xhr.status === 200) {
              const data = xhr.response ? xhr.response.body.data : xhr.data
              this.currentJobId = data.jobId[0]
            }
          })
          this.$toasted.show('Document(s) retention status processing')
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (error) {
          this.$toasted.error('Failed to update document(s) retention status')
          this.updating = false
        }
      }
      this.fetchTimeline({ pretty_id: this.prettyId })

      this.exemptedDocuments = false
      this.updating = false
      this.toggleModal(false)
    },

    toggleModal(state?: boolean) {
      this.showModal = state ?? !this.showModal
    }
  }
})
</script>

<template>
  <div class="panel-section p-bottom-8 request-expiration-date">
    <div
      class="panel-item is-flex is-align-items-center is-justify-content-space-between"
      :class="{ 'panel-item--editable': $can('read', 'admin_only_items') }"
    >
      <p class="has-text-weight-semibold">Expiration</p>
      <NrTooltip
        label="Edit request retention exemption"
        v-if="$can('read', 'admin_only_items')"
      >
        <NrIconButton
          variant="minimal-neutral"
          :icon="{ name: 'pencil-alt', type: 'regular' }"
          aria-label="Edit request retention exemption"
          @click="toggleModal(true)"
        />
      </NrTooltip>
    </div>
    <div class="m-left-8">
      <p>{{ expiration }}</p>
    </div>

    <NrModal
      title="Change request retention status"
      :show="showModal"
      :hasSecondaryButton="true"
      :disabeButtons="updating"
      secondaryButtonText="Cancel"
      primaryButtonText="Change retention status"
      @on-close-click="toggleModal(false)"
      @on-secondary-click="toggleModal(false)"
      @on-primary-click="handleSaveExempt"
    >
      <form @submit.prevent.stop>
        <div class="exemption-container">
          <NrFormInput
            class="exemption-checkbox"
            type="checkbox"
            label="Exempt request from deletion"
            name="exempt"
            v-model="newExempt"
          />
          <p class="help">
            If you exempt this request from deletion, it will not be
            automatically deleted from the portal even after the normal
            retention period passes. If a request's expiration date is already
            in the past, the request will be deleted in the next retention cycle
            unless exempted.
          </p>
        </div>

        <div class="exemption-container">
          <NrFormInput
            class="exemption-checkbox"
            type="checkbox"
            label="Exempt all documents on this request from deletion"
            name="exemptedDocuments"
            v-model="exemptedDocuments"
          />
          <p class="help">
            If you exempt documents from deletion, they will not be
            automatically deleted from the portal, even after the normal
            retention period passes. If the request is deleted and documents are
            exempt, they can still be accessed from the documents page. If a
            request's expiration date is already in the past, the documents will
            be deleted in the next retention cycle unless exempted.
          </p>
        </div>
      </form>
    </NrModal>
  </div>
</template>
<style lang="scss">
@import '@nextrequestco/components/src/styles/tokens/tokens.scss';
.exemption-container {
  display: block;
  margin-bottom: $spacing-16;

  .exemption-checkbox {
    margin-right: $spacing-6;
    margin-bottom: $spacing-4;
  }
  .help {
    margin-left: $spacing-6;
  }
}
</style>
